import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, MessageCircle, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Social Media Links
const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/martasjourney/',
  instagram: 'https://www.instagram.com/martascharle/',
  whatsapp: 'https://wa.me/36302227414',
};

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setSubscribing(true);
    try {
      await axios.post(`${API}/subscribers`, { email });
      setSubscribed(true);
      setEmail('');
      toast.success('Subscribed! Check your email for confirmation.');
    } catch (error) {
      if (error.response?.data?.detail === 'Already subscribed') {
        toast.info("You're already subscribed!");
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-stone-900 text-stone-300" data-testid="footer">
      {/* Newsletter Section */}
      <div className="border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-sm tracking-widest uppercase text-amber-500 font-medium mb-4">
              Stay Connected
            </p>
            <h3 className="font-serif text-3xl md:text-4xl text-stone-50 tracking-tight mb-4">
              Get New Stories in Your Inbox
            </h3>
            <p className="text-stone-400 mb-8">
              Subscribe to receive notifications when new stories are published. 
              No spam, just authentic tales from Nepal.
            </p>
            
            {subscribed ? (
              <div className="flex items-center justify-center gap-3 text-green-400" data-testid="subscribe-success">
                <CheckCircle size={24} />
                <span>You're subscribed! Welcome to the journey.</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="flex-1 bg-stone-800 border border-stone-700 text-stone-100 px-4 py-3 rounded-sm focus:outline-none focus:border-amber-500 placeholder:text-stone-500"
                  data-testid="subscribe-email-input"
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  className="bg-amber-500 text-stone-900 px-6 py-3 rounded-sm hover:bg-amber-400 transition-colors font-medium tracking-wide text-sm uppercase flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="subscribe-btn"
                >
                  {subscribing ? (
                    'Subscribing...'
                  ) : (
                    <>
                      <Send size={16} /> Subscribe
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand Column */}
          <div className="md:col-span-5">
            <Link to="/" className="font-serif text-3xl font-bold text-stone-50 tracking-tighter">
              Whispers of Nepal
            </Link>
            <p className="mt-6 text-stone-400 leading-relaxed max-w-md">
              Secret stories from the hidden corners of Nepal. Ancient temples, 
              mountain trails, and the untold tales of Himalayan hospitality.
            </p>
            <div className="flex items-center gap-4 mt-8">
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-[#4267B2] transition-colors"
                aria-label="Facebook"
                data-testid="social-facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-gradient-to-br hover:from-[#f09433] hover:via-[#dc2743] hover:to-[#bc1888] transition-colors"
                aria-label="Instagram"
                data-testid="social-instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href={SOCIAL_LINKS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-[#25D366] transition-colors"
                aria-label="WhatsApp"
                data-testid="social-whatsapp"
              >
                <MessageCircle size={18} />
              </a>
              <a
                href="mailto:marta.scharle@gmail.com"
                className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-red-800 transition-colors"
                aria-label="Email"
                data-testid="social-email"
              >
                <Mail size={18} />
              </a>
            </div>
          </div>

          {/* Navigation Column */}
          <div className="md:col-span-3">
            <h4 className="text-sm tracking-widest uppercase text-stone-500 font-medium mb-6">
              Explore
            </h4>
            <nav className="flex flex-col gap-3">
              <Link to="/blog" className="text-stone-400 hover:text-stone-50 transition-colors">
                Stories
              </Link>
              <Link to="/gallery" className="text-stone-400 hover:text-stone-50 transition-colors">
                Gallery
              </Link>
              <Link to="/about" className="text-stone-400 hover:text-stone-50 transition-colors">
                About
              </Link>
              <Link to="/contact" className="text-stone-400 hover:text-stone-50 transition-colors">
                Contact
              </Link>
            </nav>
          </div>

          {/* Contact Column */}
          <div className="md:col-span-4">
            <h4 className="text-sm tracking-widest uppercase text-stone-500 font-medium mb-6">
              Get in Touch
            </h4>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-red-700 mt-1 flex-shrink-0" />
                <p className="text-stone-400">
                  Kathmandu, Nepal<br />
                  Available for assignments worldwide
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-red-700 flex-shrink-0" />
                <a href="mailto:marta.scharle@gmail.com" className="text-stone-400 hover:text-stone-50 transition-colors">
                  marta.scharle@gmail.com
                </a>
              </div>
            </div>
            <Link
              to="/contact#contact-info"
              className="inline-block mt-8 bg-transparent border border-stone-600 text-stone-300 px-8 py-3 rounded-sm hover:border-stone-300 hover:text-stone-50 transition-all duration-300 text-sm tracking-wide uppercase"
            >
              Work With Me
            </Link>
          </div>
        </div>

        {/* Affiliate Link */}
        <div className="mt-12 pt-8 border-t border-stone-800 text-center">
          <p className="text-stone-500 text-sm mb-2">
            This website was built with Emergent AI
          </p>
          <a
            href="https://app.emergent.sh/register?ref=mart365898"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-500 hover:text-amber-400 text-sm transition-colors"
          >
            Build your own website with Emergent →
          </a>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-stone-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-stone-500 text-sm">
            &copy; {new Date().getFullYear()} Marta Scharle. All rights reserved.
          </p>
          <Link
            to="/admin/login"
            className="text-stone-600 hover:text-stone-400 text-sm transition-colors"
            data-testid="footer-admin-link"
          >
            Writer Access
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
